import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, Input, Card, Tabs } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'

type AuthMode = 'login' | 'register'

interface LoginFormData {
  email: string
  password: string
}

interface RegisterFormData {
  email: string
  password: string
  confirmPassword: string
}

const authTabs = [
  { id: 'login', label: 'Login' },
  { id: 'register', label: 'Register' },
]

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, register: registerUser } = useAuth()

  const initialTab = searchParams.get('tab')
  const [mode, setMode] = useState<AuthMode>(
    initialTab === 'register' ? 'register' : 'login'
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Store selected plan for post-registration Stripe flow
  useEffect(() => {
    const plan = searchParams.get('plan')
    if (plan) {
      localStorage.setItem('selected_plan', plan)
    }
  }, [searchParams])

  const loginForm = useForm<LoginFormData>()
  const registerForm = useForm<RegisterFormData>()

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await login(data.email, data.password)
      navigate('/create')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegister = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' })
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await registerUser(data.email, data.password)
      navigate('/create')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleModeChange = (newMode: string) => {
    setMode(newMode as AuthMode)
    setError(null)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-medium">
            Beacon
          </Link>
          <p className="text-secondary-text mt-2">
            AI-powered social media management
          </p>
        </div>

        <Card>
          <Tabs tabs={authTabs} activeTab={mode} onTabChange={handleModeChange} />

          <div className="mt-6">
            {error && (
              <div className="mb-4 p-3 border border-error bg-red-50">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {mode === 'login' ? (
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={loginForm.formState.errors.email?.message}
                />

                <Input
                  label="Password"
                  type="password"
                  {...loginForm.register('password', {
                    required: 'Password is required',
                  })}
                  error={loginForm.formState.errors.password?.message}
                />

                <div className="flex justify-end">
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm text-secondary-text hover:text-primary-text"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button type="submit" loading={isLoading} className="w-full">
                  Login
                </Button>
              </form>
            ) : (
              <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  {...registerForm.register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={registerForm.formState.errors.email?.message}
                />

                <Input
                  label="Password"
                  type="password"
                  {...registerForm.register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                  error={registerForm.formState.errors.password?.message}
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  {...registerForm.register('confirmPassword', {
                    required: 'Please confirm your password',
                  })}
                  error={registerForm.formState.errors.confirmPassword?.message}
                />

                <Button type="submit" loading={isLoading} className="w-full">
                  Create Account
                </Button>
              </form>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
